import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import "./lightning-table.css";

// A row per turbine per strike runs to tens of thousands over a multi-year period, and a
// table that puts them all in the DOM makes every layout of the page cost seconds. With
// `virtualize` only the rows near the viewport are rendered; the rest is accounted for by a
// spacer row above and below, so scrollbar and scroll position behave normally.
//
// The row height is measured from a real row rather than assumed: this only has to be off by
// half a pixel for the spacers to drift by thousands of pixels over tens of thousands of rows.
const FALLBACK_ROW_HEIGHT_PX = 34;
const OVERSCAN_ROWS = 15;

export default function LightningTable({
  loading,
  items,
  fields,
  className,
  customSortFunction,
  customRenderFunction = null,
  virtualize = false,
}) {
  const [sortByColumn, setSortByColumn] = useState(fields[0]?.key || "");
  const [ascDesc, setAscDesc] = useState(1);
  const scrollRef = useRef(null);
  const [viewport, setViewport] = useState({ start: 0, end: OVERSCAN_ROWS * 4 });
  const [rowHeight, setRowHeight] = useState(FALLBACK_ROW_HEIGHT_PX);

  const sortedItems = useMemo(() => {
    const defaultSort = (a, b) => {
      const aValue = a[sortByColumn];
      const bValue = b[sortByColumn];

      if (aValue === bValue) return 0;
      if (aValue === null || aValue === undefined) return -1 * ascDesc;
      if (bValue === null || bValue === undefined) return ascDesc;
      if (sortByColumn === "timestamp") return (new Date(b[sortByColumn]) - new Date(a[sortByColumn])) * ascDesc;



      if (typeof aValue === "string") return aValue.localeCompare(bValue) * ascDesc;

      return aValue > bValue ? -1 * ascDesc : 1 * ascDesc;
    };

    const sortFunction = customSortFunction || defaultSort;
    return [...items].sort(sortFunction);
  }, [items, customSortFunction, sortByColumn, ascDesc]);

  const clickedSort = useCallback(
    (headerName) => {
      if (headerName === sortByColumn) {
        if (ascDesc === -1) {
          setSortByColumn(null);
          setAscDesc(1);
        } else {
          setAscDesc(-ascDesc);
        }
      } else {
        setSortByColumn(headerName);
        setAscDesc(1);
      }
    },
    [sortByColumn, ascDesc]
  );

  // The table body scrolls inside itself (max-height + overflow-y in lightning-table.css),
  // so the window's scroll position says nothing about which rows are on screen - the
  // scroller's own scrollTop does.
  const recomputeViewport = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const firstVisible = Math.max(
      0,
      Math.floor(el.scrollTop / rowHeight) - OVERSCAN_ROWS
    );
    const visibleCount =
      Math.ceil(el.clientHeight / rowHeight) + OVERSCAN_ROWS * 2;

    setViewport((prev) =>
      prev.start === firstVisible && prev.end === firstVisible + visibleCount
        ? prev
        : { start: firstVisible, end: firstVisible + visibleCount }
    );
  }, [rowHeight]);

  useEffect(() => {
    if (!virtualize) return;

    const el = scrollRef.current;
    if (!el) return;

    recomputeViewport();
    const onScroll = () => recomputeViewport();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [virtualize, recomputeViewport, sortedItems]);

  // Take the height from a rendered row once there is one to measure.
  useEffect(() => {
    if (!virtualize) return;

    const row = scrollRef.current?.querySelector("tbody tr:not([aria-hidden])");
    const measured = row?.getBoundingClientRect().height;
    if (measured && Math.abs(measured - rowHeight) > 0.5) setRowHeight(measured);
  }, [virtualize, rowHeight, sortedItems]);

  // Sorting reorders every row, so the window has to go back to the top with them.
  useEffect(() => {
    if (virtualize && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [virtualize, sortByColumn, ascDesc]);

  const start = virtualize ? Math.min(viewport.start, Math.max(0, sortedItems.length - 1)) : 0;
  const end = virtualize ? Math.min(viewport.end, sortedItems.length) : sortedItems.length;
  const visibleItems = virtualize ? sortedItems.slice(start, end) : sortedItems;
  const spacerAbove = start * rowHeight;
  const spacerBelow = Math.max(0, (sortedItems.length - end) * rowHeight);

  return (
    <div className="lightning-table-wrapper">
      <div className={`lightning-table ${className}`} ref={scrollRef}>
        <table>
          <thead>
            <tr>
              {fields.map(({ key, title }) => (
                <th key={key} onClick={() => clickedSort(key)}>
                  <span className="title">{title}</span>                  <span className="arrows">
                    {sortByColumn === key || (key === "turbine_name" && sortByColumn === null) ? (
                      ascDesc === -1 ? "↓" : "↑"
                    ) : (
                      "↑↓"
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {spacerAbove > 0 && (
              <tr style={{ height: spacerAbove }} aria-hidden="true" />
            )}
            {visibleItems.map((item, idx) => {
              const index = start + idx;
              if (customRenderFunction) return customRenderFunction(item, index, sortByColumn);

              return (
                <tr key={item.id || index}>
                  {fields.map(({ key }) => (
                    <td key={key}>{item[key]}</td>
                  ))}
                </tr>
              );
            })}
            {spacerBelow > 0 && (
              <tr style={{ height: spacerBelow }} aria-hidden="true" />
            )}
            {!sortedItems.length && !loading && (
              <tr>
                <td colSpan={fields.length} style={{ textAlign: "center" }}>
                  No data matches your current selection
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
